/*
 * CloudflareMigrationPanel — Cloudinary → Cloudflare R2 migration UI.
 *
 * Three premium cards, gated to SuperDev only:
 *
 *   ① Scan migration plan
 *      Read-only preview. Shows the live counts per category
 *      (Articles / Employees / IT-Lab devices / Students / Programme
 *      images / Schools), a warning when the storage mode is still
 *      "cloud", and a Show-details toggle that renders a thumbnail
 *      grid of the assets that are about to move.
 *
 *   ② Run migration
 *      Type-to-confirm modal (operator types literal MIGRATE before
 *      the Start button enables). Once confirmed, polls
 *      POST /api/storage/migrate?batchSize=10 until done. Per-item
 *      successes / failures accumulate live with thumbnails so the
 *      operator can spot a row that landed wrong and reproduce it.
 *
 *   ③ Verify migration
 *      Read-only post-check. Bucketises every URL column into
 *      cloudinary / r2 / local / other / empty per category, with
 *      a big green "All clear" card when zero rows still point at
 *      Cloudinary.
 *
 * Safety details encoded in the UI:
 *   - Type-to-confirm prevents the muscle-memory Enter-key delete
 *   - Polling loop uses a stopRef set on unmount so a fast tab close
 *     can't keep firing batches against a torn-down component
 *   - Migrate button is disabled while a batch is in flight (no
 *     double-submit) and when scan reports r2Ready === false
 *   - Defensive <Thumb> handles null / 404 URLs gracefully — a
 *     single broken thumbnail never breaks the list
 *
 * Premium aesthetic — see CloudflareMigrationPanel.module.css for
 * the ocean/teal palette + entrance / hover / pulse animations.
 * Reduced-motion is honoured throughout.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./CloudflareMigrationPanel.module.css";

/** Helpers for HTTP calls — every request carries X-User-Role from
 *  the session, mirroring the convention used elsewhere in
 *  SettingsPage. The backend additionally enforces SuperDev-only
 *  on this endpoint so spoofing the header isn't sufficient. */
function authHeaders() {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!raw) return { "Content-Type": "application/json" };
    const u = JSON.parse(raw);
    const h = { "Content-Type": "application/json" };
    if (u?.role)        h["X-User-Role"]        = u.role;
    if (u?.username)    h["X-Username"]         = u.username;
    if (u?.employeeCode)h["X-Employee-Code"]    = u.employeeCode;
    return h;
  } catch {
    return { "Content-Type": "application/json" };
  }
}

/** Category labels for the UI — sourced once so a future rename only
 *  touches one place. Order matches the backend's per-category
 *  iteration so the UI columns line up with the audit log. */
const CATEGORY_LABEL = {
  articles:      "Articles",
  employees:     "Employee photos",
  labDevices:    "IT-lab device photos",
  students:      "Student photos",
  programImages: "Programme images",
  schools:       "School logos",
};

const formatNumber = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString();
};

const trim = (s, n = 38) => {
  if (!s) return "";
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
};

/** Defensive thumbnail — renders a 48×48 image with a gradient
 *  placeholder for null / loading / broken URLs. The placeholder
 *  swap on onError prevents one missing asset from breaking the
 *  whole list's reflow. */
function Thumb({ src, alt }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return <div className={s.thumbPlaceholder} aria-hidden />;
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      className={s.thumb}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
    />
  );
}

export default function CloudflareMigrationPanel({ role }) {
  // The whole panel is SuperDev-only. The Maintenance tab is already
  // gated to SuperAdmin+, so we add the inner SuperDev check here
  // so a SuperAdmin who somehow lands on the page sees nothing
  // (defence-in-depth — the backend also refuses non-SuperDev).
  if (role !== "SuperDev") return null;

  return <Panel />;
}

function Panel() {
  // ── Card 1 — scan ──────────────────────────────────────────────
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError,   setScanError]   = useState("");
  const [scanResult,  setScanResult]  = useState(null);
  const [scanDetails, setScanDetails] = useState(false);

  const runScan = useCallback(async () => {
    setScanLoading(true); setScanError("");
    try {
      const r = await fetch(`${API_BASE}/api/storage/migrate-scan`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(await extractErrorMessage(r, `Scan failed (${r.status})`));
      setScanResult(await r.json());
    } catch (e) { setScanError(e.message || "Scan failed"); }
    finally    { setScanLoading(false); }
  }, []);

  // ── Card 2 — migrate ───────────────────────────────────────────
  const [confirmOpen,    setConfirmOpen]    = useState(false);
  const [confirmInput,   setConfirmInput]   = useState("");
  const [migrating,      setMigrating]      = useState(false);
  const [migrateError,   setMigrateError]   = useState("");
  const [progress,       setProgress]       = useState({ migrated: 0, failed: 0, remaining: 0, totalPlanned: 0 });
  const [migrateDone,    setMigrateDone]    = useState(false);
  const [items,          setItems]          = useState([]);   // { id, name, oldUrl, newUrl, ok, category, error }
  const [itemsDetails,   setItemsDetails]   = useState(false);
  /** Cancellation handle — set on unmount so the polling loop
   *  short-circuits if the user navigates away mid-batch. The ref
   *  IDENTITY is stable for the component's lifetime; only the
   *  `.stop` field flips. We capture the ref once at mount-time and
   *  use the captured reference in the cleanup so React's
   *  exhaustive-deps lint doesn't flag a stale closure (the ref
   *  object itself can't change, only what it points at). */
  const stopRef = useRef({ stop: false });

  useEffect(() => {
    const handle = stopRef.current;
    handle.stop = false;
    return () => { handle.stop = true; };
  }, []);

  useEffect(() => {
    if (!confirmOpen) return;
    const fn = e => { if (e.key === "Escape") setConfirmOpen(false); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [confirmOpen]);

  const startMigration = useCallback(async () => {
    if (confirmInput.trim().toUpperCase() !== "MIGRATE") return;
    setConfirmOpen(false);
    setConfirmInput("");
    setMigrating(true);
    setMigrateDone(false);
    setMigrateError("");
    setItems([]);
    setProgress({
      migrated: 0, failed: 0,
      remaining: scanResult?.totalRemaining || 0,
      totalPlanned: scanResult?.totalRemaining || 0,
    });

    // Safety net — if the backend ever streams more than MAX_BATCHES
    // batches it's a sign something's looping; bail out so the page
    // doesn't peg the browser CPU forever.
    const MAX_BATCHES = 2000;
    let batches = 0;
    try {
      while (!stopRef.current.stop && batches < MAX_BATCHES) {
        batches++;
        const r = await fetch(
          `${API_BASE}/api/storage/migrate?batchSize=10`,
          { method: "POST", headers: authHeaders() }
        );
        if (!r.ok) throw new Error(await extractErrorMessage(r, `Migration failed (${r.status})`));
        const data = await r.json();

        // Flatten per-category successes + failures into a single
        // running list so the UI scrolls cleanly without per-category
        // hierarchy.
        const newItems = [];
        for (const key of Object.keys(CATEGORY_LABEL)) {
          const c = data?.[key];
          if (!c) continue;
          for (const ok of (c.successes || [])) {
            newItems.push({ ...ok, ok: true, category: key });
          }
          for (const bad of (c.failures || [])) {
            newItems.push({ ...bad, ok: false, category: key });
          }
        }
        if (newItems.length > 0) {
          setItems(prev => [...prev, ...newItems]);
        }

        const batchMigrated = newItems.filter(x => x.ok).length;
        const batchFailed   = newItems.filter(x => !x.ok).length;
        setProgress(p => ({
          totalPlanned: p.totalPlanned,
          migrated:     p.migrated + batchMigrated,
          failed:       p.failed   + batchFailed,
          remaining:    data.remaining || 0,
        }));

        if (data.done) break;
      }
      setMigrateDone(true);
    } catch (e) {
      setMigrateError(e.message || "Migration failed");
    } finally {
      setMigrating(false);
    }
  }, [confirmInput, scanResult]);

  // ── Card 3 — verify ────────────────────────────────────────────
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError,   setVerifyError]   = useState("");
  const [verifyResult,  setVerifyResult]  = useState(null);

  const runVerify = useCallback(async () => {
    setVerifyLoading(true); setVerifyError("");
    try {
      const r = await fetch(`${API_BASE}/api/storage/migrate-verify`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(await extractErrorMessage(r, `Verify failed (${r.status})`));
      setVerifyResult(await r.json());
    } catch (e) { setVerifyError(e.message || "Verify failed"); }
    finally    { setVerifyLoading(false); }
  }, []);

  // ── Derived values ─────────────────────────────────────────────
  const r2NotReady = scanResult && !scanResult.r2Ready;
  const modeIsCloud = scanResult && scanResult.storageMode !== "r2";

  const migrateDisabled =
        !scanResult
     || scanResult.totalRemaining === 0
     || !scanResult.r2Ready
     || migrating;

  // Progress bar percentage — clamped to [0, 100] so a slightly stale
  // totalPlanned (from a scan run before someone added new uploads)
  // can't spike the bar above 100.
  const progressPct = useMemo(() => {
    const done = progress.migrated + progress.failed;
    if (progress.totalPlanned <= 0) return migrateDone ? 100 : 0;
    return Math.max(0, Math.min(100, Math.round((done / progress.totalPlanned) * 100)));
  }, [progress, migrateDone]);

  return (
    <div className={s.wrap}>
      {/* ── Card 1 ─────────────────────────────────────────── */}
      <section className={s.card}>
        <header className={s.cardHead}>
          <div>
            <div className={s.cardEyebrow}>① Scan</div>
            <h3 className={s.cardTitle}>Scan migration plan</h3>
            <p className={s.cardHint}>
              Read-only preview. Counts every image / logo / media URL
              still hosted on Cloudinary, per category. Run this first
              to see the size of the move before clicking Migrate.
            </p>
          </div>
          <button
            className={s.primaryBtn}
            onClick={runScan}
            disabled={scanLoading}
          >
            {scanLoading ? "Scanning…" : "🔍 Scan migration plan"}
          </button>
        </header>

        {scanError && <div className={s.errorBanner}>{scanError}</div>}

        {scanResult && (
          <>
            {modeIsCloud && (
              <div className={s.warningBanner}>
                Storage mode is still <code>{scanResult.storageMode}</code>.
                Set <code>APP_STORAGE_MODE=r2</code> in Render env before
                migrating — new uploads will keep landing on Cloudinary
                until that flag flips.
              </div>
            )}
            {r2NotReady && (
              <div className={s.errorBanner}>
                R2 is not wired. Set <code>R2_ENDPOINT</code>,
                <code>R2_ACCESS_KEY_ID</code>, <code>R2_SECRET_ACCESS_KEY</code>,
                <code>R2_BUCKET</code>, and <code>R2_PUBLIC_BASE_URL</code>
                env vars before migrating.
              </div>
            )}

            <div className={s.statGrid}>
              {Object.entries(CATEGORY_LABEL).map(([key, label]) => {
                const c = scanResult.categories?.[key] || { total: 0, remaining: 0 };
                return (
                  <div key={key} className={s.stat}>
                    <div className={s.statLabel}>{label}</div>
                    <div className={s.statNumber}>
                      {formatNumber(c.remaining)}
                      <span className={s.statSuffix}> / {formatNumber(c.total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={s.totalRow}>
              <span>Total remaining on Cloudinary</span>
              <strong>{formatNumber(scanResult.totalRemaining)}</strong>
            </div>

            <button
              className={s.toggleBtn}
              onClick={() => setScanDetails(v => !v)}
            >
              {scanDetails ? "▾ Hide details" : "▸ Show details"}
            </button>

            {scanDetails && (
              <div className={s.detailsList}>
                {Object.entries(CATEGORY_LABEL).map(([key, label]) => {
                  const c = scanResult.categories?.[key];
                  if (!c || (c.sample || []).length === 0) return null;
                  return (
                    <div key={key} className={s.detailsGroup}>
                      <div className={s.detailsGroupTitle}>{label}</div>
                      <ul className={s.detailsRows}>
                        {(c.sample || []).map((row) => (
                          <li key={`${key}-${row.id}`} className={s.detailsRow}>
                            <Thumb src={row.url} alt={row.name} />
                            <div className={s.detailsRowText}>
                              <div className={s.detailsRowName}>{trim(row.name, 60)}</div>
                              <div className={s.detailsRowUrl}>{trim(row.url, 80)}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Card 2 ─────────────────────────────────────────── */}
      <section className={s.card}>
        <header className={s.cardHead}>
          <div>
            <div className={s.cardEyebrow}>② Migrate</div>
            <h3 className={s.cardTitle}>Run migration</h3>
            <p className={s.cardHint}>
              Migrates every remaining Cloudinary asset to R2 in
              10-item batches. Original Cloudinary assets are NOT
              deleted — rollback is a single env-var flip until you
              run the Step-8 cleanup.
            </p>
          </div>
          <button
            className={s.primaryBtn}
            onClick={() => setConfirmOpen(true)}
            disabled={migrateDisabled}
          >
            {migrating ? "Migrating…" : "▶ Run migration"}
          </button>
        </header>

        {migrateError && <div className={s.errorBanner}>{migrateError}</div>}

        {(migrating || progress.migrated + progress.failed > 0) && (
          <>
            <div className={s.progressBarOuter}>
              <div
                className={`${s.progressBarInner} ${migrating ? s.progressBarRunning : ""}`}
                style={{ width: `${progressPct}%` }}
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className={s.progressCounters}>
              <span className={s.counterOk}>✓ Migrated <strong>{formatNumber(progress.migrated)}</strong></span>
              <span className={s.counterFail}>✗ Failed <strong>{formatNumber(progress.failed)}</strong></span>
              <span className={s.counterPend}>Remaining <strong>{formatNumber(progress.remaining)}</strong></span>
            </div>
          </>
        )}

        {migrateDone && !migrating && (
          <div className={s.successBanner}>
            🎉 Migration finished. Run Verify (Card 3) to confirm
            every column is now on R2.
          </div>
        )}

        {items.length > 0 && (
          <>
            <button
              className={s.toggleBtn}
              onClick={() => setItemsDetails(v => !v)}
            >
              {itemsDetails ? "▾ Hide migrated items" : "▸ Show migrated items"}
              <span className={s.toggleBtnCount}>
                ({items.length})
              </span>
            </button>
            {itemsDetails && (
              <ul className={s.itemList}>
                {items.map((it, idx) => (
                  <li
                    key={`${it.category}-${it.id}-${idx}`}
                    className={`${s.itemRow} ${it.ok ? s.itemOk : s.itemFail}`}
                  >
                    <Thumb src={it.ok ? it.newUrl : it.oldUrl || it.url} alt={it.name} />
                    <div className={s.itemText}>
                      <div className={s.itemName}>
                        <span className={s.itemBadge}>
                          {CATEGORY_LABEL[it.category] || it.category}
                        </span>
                        {trim(it.name, 50)}
                      </div>
                      <div className={s.itemUrl}>
                        {it.ok
                          ? trim(it.newUrl, 90)
                          : <span className={s.itemError}>{it.error || "Failed"}</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {/* ── Card 3 ─────────────────────────────────────────── */}
      <section className={s.card}>
        <header className={s.cardHead}>
          <div>
            <div className={s.cardEyebrow}>③ Verify</div>
            <h3 className={s.cardTitle}>Verify migration</h3>
            <p className={s.cardHint}>
              Re-counts every URL column and buckets each into
              cloudinary / r2 / local / other. A green "all clear" banner
              confirms zero rows still point at Cloudinary.
            </p>
          </div>
          <button
            className={s.primaryBtn}
            onClick={runVerify}
            disabled={verifyLoading}
          >
            {verifyLoading ? "Verifying…" : "✓ Run verification"}
          </button>
        </header>

        {verifyError && <div className={s.errorBanner}>{verifyError}</div>}

        {verifyResult && (
          <>
            {verifyResult.allCloudinaryClear ? (
              <div className={s.allClear}>
                <div className={s.allClearIcon}>✨</div>
                <div>
                  <div className={s.allClearTitle}>All clear</div>
                  <div className={s.allClearSub}>
                    Zero rows still point at Cloudinary. Safe to proceed
                    with Step 8 (Cloudinary decommission) when ready.
                  </div>
                </div>
              </div>
            ) : (
              <div className={s.warningBanner}>
                Some rows are still on Cloudinary. Re-run Scan + Migrate —
                the system is idempotent so only the residuals will move.
              </div>
            )}

            <div className={s.bucketGrid}>
              {Object.entries(CATEGORY_LABEL).map(([key, label]) => {
                const b = verifyResult.categories?.[key] || {};
                return (
                  <div key={key} className={s.bucket}>
                    <div className={s.bucketTitle}>{label}</div>
                    <div className={s.bucketRows}>
                      <div className={s.bucketRow}>
                        <span>Total</span><strong>{formatNumber(b.total)}</strong>
                      </div>
                      <div className={`${s.bucketRow} ${b.cloudinary > 0 ? s.bucketRowBad : ""}`}>
                        <span>Cloudinary</span><strong>{formatNumber(b.cloudinary)}</strong>
                      </div>
                      <div className={`${s.bucketRow} ${b.r2 > 0 ? s.bucketRowGood : ""}`}>
                        <span>R2</span><strong>{formatNumber(b.r2)}</strong>
                      </div>
                      <div className={s.bucketRow}>
                        <span>Local</span><strong>{formatNumber(b.local)}</strong>
                      </div>
                      <div className={s.bucketRow}>
                        <span>Other</span><strong>{formatNumber(b.other)}</strong>
                      </div>
                      <div className={s.bucketRow}>
                        <span>Empty</span><strong>{formatNumber(b.empty)}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* ── Confirm modal ──────────────────────────────────── */}
      {confirmOpen && (
        <div className={s.modalBackdrop}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={s.modalTitle}>Run migration?</h3>
            <p className={s.modalBody}>
              Every asset still on Cloudinary will be re-uploaded to
              R2. The DB column for each row flips to the new R2 URL
              <strong> only after</strong> the upload succeeds, so a
              mid-batch failure never leaves a row pointing at a
              missing object.
            </p>
            <p className={s.modalBody}>
              The original Cloudinary asset is left in place — rollback
              is a single env-var flip on Render.
            </p>
            <p className={s.modalBody}>
              To proceed, type <code>MIGRATE</code> into the box.
            </p>
            <input
              type="text"
              autoFocus
              className={s.modalInput}
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder="Type MIGRATE"
            />
            <div className={s.modalActions}>
              <button
                className={s.secondaryBtn}
                onClick={() => { setConfirmOpen(false); setConfirmInput(""); }}
              >
                Cancel
              </button>
              <button
                className={s.primaryBtnDanger}
                onClick={startMigration}
                disabled={confirmInput.trim().toUpperCase() !== "MIGRATE"}
              >
                ▶ Start migration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
